import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppConfigProvider, useAppConfig } from "@/contexts/AppConfigContext";
import Header from "@/components/Header";
import RequireRole from "@/components/RequireRole";
import AgeGate from "@/components/AgeGate";
import SommelierChat from "@/components/SommelierChat";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Privacy from "@/pages/Privacy";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminWines from "@/pages/admin/AdminWines";
import AdminWineEditor from "@/pages/admin/AdminWineEditor";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminOutbox from "@/pages/admin/AdminOutbox";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminFeatured from "@/pages/admin/AdminFeatured";
import AdminImports from "@/pages/admin/AdminImports";
import AdminQueues from "@/pages/admin/AdminQueues";
import AdminMergeProposals from "@/pages/admin/AdminMergeProposals";
import AdminMexal from "@/pages/admin/AdminMexal";
import AdminMissingPrice from "@/pages/admin/AdminMissingPrice";
import AdminProducerWebsites from "@/pages/admin/AdminProducerWebsites";
import AdminProducerMismatches from "@/pages/admin/AdminProducerMismatches";
import AdminMapZones from "@/pages/admin/AdminMapZones";
import AdminPrintCatalog from "@/pages/admin/AdminPrintCatalog";
import AdminPaperSections from "@/pages/admin/AdminPaperSections";
import AdminMexalCsv from "@/pages/admin/AdminMexalCsv";
import AdminExternalSync from "@/pages/admin/AdminExternalSync";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminSommelier from "@/pages/admin/AdminSommelier";
import AdminGdpr from "@/pages/admin/AdminGdpr";
import AdminMexalImport from "@/pages/admin/AdminMexalImport";
import AdminDataQuality from "@/pages/admin/AdminDataQuality";
import AdminReviewHub from "@/pages/admin/AdminReviewHub";
import AdminDistillati from "@/pages/admin/AdminDistillati";
import AdminWineFromPhoto from "@/pages/admin/AdminWineFromPhoto";
import StaffLogin from "@/pages/StaffLogin";
import StaffQrEntry from "@/pages/StaffQrEntry";
import StaffCatalog from "@/pages/StaffCatalog";
import MapExplore from "@/pages/MapExplore";
import Spirits from "@/pages/Spirits";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";

function PublicShell({ children }) {
    const { publicCfg } = useAppConfig();
    const { user } = useAuth();
    const restaurantUrl = publicCfg?.restaurant_site_url || "";
    return (
        <>
            <Header />
            {children}
            <footer className="co-container py-10 co-hairline mt-16" data-testid="public-footer">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="co-caption">
                        Casa Orterie · Stazzona
                    </p>
                    <nav className="flex items-center gap-5 text-xs text-[hsl(var(--muted-foreground))] flex-wrap justify-center">
                        {restaurantUrl && (
                            <a href={restaurantUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-restaurant-link" className="hover:text-[hsl(var(--primary))]">
                                Sito Casa Orterie
                            </a>
                        )}
                        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[hsl(var(--primary))]">
                            Informativa privacy
                        </Link>
                    </nav>
                </div>
                {/* Fase 7.6: Accedi/Registrati sono in footer ma resi
                    più visibili come pill outline bordeaux (non più link testuali). */}
                {!user && (
                    <div className="mt-6 flex items-center justify-center gap-3 flex-wrap" data-testid="footer-auth-cta">
                        <Link
                            to="/login"
                            data-testid="footer-login-btn"
                            className="inline-flex items-center justify-center min-w-[140px] px-6 py-2.5 border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full text-sm font-medium hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                        >
                            Accedi
                        </Link>
                        <Link
                            to="/register"
                            data-testid="footer-register-btn"
                            className="inline-flex items-center justify-center min-w-[140px] px-6 py-2.5 border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full text-sm font-medium hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                        >
                            Registrati
                        </Link>
                    </div>
                )}
            </footer>
            {/* Chat "Consigliami" — sempre presente sulle pagine pubbliche. */}
            <SommelierChat />
        </>
    );
}

function AuthShell({ children }) {
    return (
        <>
            <Header />
            {children}
        </>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <AppConfigProvider>
                    <BrowserRouter>
                    {/* Age gate 18+ (Fase 7.1): overlay al primo accesso.
                        Persist in localStorage. Bypass automatico se admin/staff loggati. */}
                    <AgeGate />
                    <Routes>
                        <Route path="/" element={<PublicShell><Home /></PublicShell>} />
                        <Route path="/carta" element={<PublicShell><Catalog /></PublicShell>} />
                        <Route path="/distillati" element={<PublicShell><Spirits /></PublicShell>} />
                        <Route path="/mappa" element={<PublicShell><MapExplore /></PublicShell>} />
                        <Route path="/privacy" element={<PublicShell><Privacy /></PublicShell>} />
                        <Route path="/account" element={<PublicShell><Account /></PublicShell>} />
                        <Route path="/login" element={<AuthShell><Login /></AuthShell>} />
                        <Route path="/register" element={<AuthShell><Register /></AuthShell>} />
                        <Route path="/verifica-email" element={<AuthShell><VerifyEmail /></AuthShell>} />
                        <Route path="/password-dimenticata" element={<AuthShell><ForgotPassword /></AuthShell>} />
                        <Route path="/reimposta-password" element={<AuthShell><ResetPassword /></AuthShell>} />

                        {/* Staff (PIN + QR + guarded catalog) */}
                        <Route path="/staff/login" element={<StaffLogin />} />
                        <Route path="/staff/qr/:token" element={<StaffQrEntry />} />
                        <Route
                            path="/staff"
                            element={
                                <RequireRole roles={["staff", "admin"]} redirectTo="/staff/login">
                                    <StaffCatalog />
                                </RequireRole>
                            }
                        />

                        <Route
                            path="/admin"
                            element={
                                <RequireRole roles="admin">
                                    <AdminLayout />
                                </RequireRole>
                            }
                        >
                            <Route index element={<AdminWines />} />
                            <Route path="vini" element={<AdminWines />} />
                            <Route path="vini/nuovo" element={<AdminWineEditor />} />
                            <Route path="vini/:id" element={<AdminWineEditor />} />
                            <Route path="in-evidenza" element={<AdminFeatured />} />
                            <Route path="imports" element={<AdminImports />} />
                            <Route path="mexal" element={<AdminMexal />} />
                            <Route path="senza-prezzo" element={<AdminMissingPrice />} />
                            <Route path="siti-produttore" element={<AdminProducerWebsites />} />
                            <Route path="mismatch-produttori" element={<AdminProducerMismatches />} />
                            <Route path="data-quality" element={<AdminDataQuality />} />
                            <Route path="segnalazioni" element={<AdminReviewHub />} />
                            <Route path="distillati" element={<AdminDistillati />} />
                            <Route path="aggiungi-foto" element={<AdminWineFromPhoto />} />
                            <Route path="mexal-import" element={<AdminMexalImport />} />
                            <Route path="mexal-csv" element={<AdminMexalCsv />} />
                            <Route path="proposte-merge" element={<AdminMergeProposals />} />
                            <Route path="code" element={<AdminQueues />} />
                            <Route path="prezzi" element={<AdminPricing />} />
                            <Route path="sync-esterno" element={<AdminExternalSync />} />
                            <Route path="backup" element={<AdminBackups />} />
                            <Route path="sommelier" element={<AdminSommelier />} />
                            <Route path="configurazione" element={<AdminConfig />} />
                            <Route path="mappa" element={<AdminMapZones />} />
                            <Route path="sezioni-carta" element={<AdminPaperSections />} />
                            <Route path="stampa-carta" element={<AdminPrintCatalog />} />
                            <Route path="utenti" element={<AdminUsers />} />
                            <Route path="gdpr" element={<AdminGdpr />} />
                            <Route path="outbox" element={<AdminOutbox />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-center" richColors />
                <PwaUpdateBanner />
                <PWAInstallBanner />
                </AppConfigProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
